import { FormattedMessage } from 'react-intl';
import { useQuery, gql } from '@apollo/client';
import {
  Alert,
  Loading,
  Map,
  LngLat,
  CoordinatesProperties,
  MapTypes,
  ViewTypes,
  DEFAULT_FILTERS,
  ProhibitedZoneProperties
} from '../../components';
import StaticStationMap from './StaticStationMap';
import {
  FEATURE_TOGGLES_QUERY,
  STATION_MAP_QUERY,
  DOCK_GROUPS_QUERY,
  FREE_FLOATING_VEHICLES_QUERY,
  PROHIBITED_ZONES_QUERY
} from './graphql';
import {
  stationMapQuery,
  stationMapQueryVariables,
  completedTripSubscription,
  completedTripSubscriptionVariables,
  DockGroupState,
  VehicleCategory2,
  featureTogglesQuery,
  featureTogglesQueryVariables,
  DockGroups_dockGroups_availabilityInfo,
  DockGroups_dockGroups_availabilityInfo_availableVehicleCategories,
  DockGroups,
  DockGroupsVariables,
  FreeFloatingVehicles,
  FreeFloatingVehiclesVariables,
  ProhibitedZones
} from '../../core-types';
import { Subscription } from '@apollo/client/react/components';
import { useStyledTheme } from '../../lib/hooks';
import { useSystem } from '../../lib/core/useSystem';
import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp.js';
import { isAreaWithinTheSchedule } from '../ProhibitedZones/helper';

const TOKEN =
  'pk.eyJ1IjoidXJiYW5zaGFyaW5nIiwiYSI6ImNqbHc2YnFkbzB5ZmMzd3Axem9ja2doOHIifQ.Ml2393RAIwLoLpJHm0Cx4A';

const SHOW_STATIC_MAP = !mapboxgl.supported();

const TRIP_SUBSCRIPTION = gql`
  subscription completedTripSubscription($systemId: ID!) {
    completedTrip(systemId: $systemId) {
      id
      startedAt
      endedAt
      direction {
        duration
        distance
        geometry {
          lat
          lng
        }
      }
      startDockGroup {
        id
      }
      endDockGroup {
        id
      }
    }
  }
`;

const calculateAvailableVehicles = (
  availabilityInfo: DockGroups_dockGroups_availabilityInfo,
  filters: typeof DEFAULT_FILTERS
): number => {
  if (!availabilityInfo.availableVehicleCategories?.length) {
    return availabilityInfo.availableVehicles || 0;
  }

  return availabilityInfo.availableVehicleCategories.reduce((res, aVC) => {
    if (aVC.category && filters[aVC.category]) {
      return res + aVC.count;
    }
    return res;
  }, 0);
};

const calculateAvailableDocks = (
  availabilityInfo: DockGroups_dockGroups_availabilityInfo,
  filters: typeof DEFAULT_FILTERS
) => {
  const availablePhysicalDocks = filters.physical
    ? availabilityInfo.availablePhysicalDocks || 0
    : 0;
  const availableVirtualDocks = filters.virtual
    ? availabilityInfo.availableVirtualDocks || 0
    : 0;

  return availableVirtualDocks + availablePhysicalDocks;
};

type StationMapProps = {
  view: ViewTypes;
  mapType: MapTypes;
  enableAnimatedTrips: boolean;
  disableMapControls?: boolean;
  mapHeight?: string | number;
};

const StationMap: FC<StationMapProps> = props => {
  const {
    view,
    mapType,
    enableAnimatedTrips,
    disableMapControls,
    mapHeight = '80vh'
  } = props;
  const theme = useStyledTheme();
  const system = useSystem();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [batteryChargeFilter, setBatteryChargeFilter] = React.useState<
    number[]
  >([0, 100]);
  const switchFilterValue = useCallback((key: keyof typeof DEFAULT_FILTERS) => {
    setFilters(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const handleBatteryChargeFilterChange = (
    event: any,
    newValue: number | number[]
  ) => {
    setBatteryChargeFilter(newValue as number[]);
  };

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
  }, [view]);

  const { data: stationMapQueryData } = useQuery<
    stationMapQuery,
    stationMapQueryVariables
  >(STATION_MAP_QUERY, {
    variables: {
      systemId: system.id
    }
  });

  const {
    data: dockGroupsQueryData,
    loading: dockGroupsQueryLoading,
    error: dockGroupsQueryError
  } = useQuery<DockGroups, DockGroupsVariables>(DOCK_GROUPS_QUERY, {
    variables: {
      systemId: system.id
    }
  });

  const {
    data: featureTogglesData,
    loading: featureTogglesLoading,
    error: featureTogglesError
  } = useQuery<featureTogglesQuery, featureTogglesQueryVariables>(
    FEATURE_TOGGLES_QUERY,
    {
      variables: {
        systemId: system.id
      }
    }
  );

  const {
    data: freeFloatingVehiclesQueryData,
    loading: freeFloatingVehiclesLoading,
    error: freeFloatingVehiclesError
  } = useQuery<FreeFloatingVehicles, FreeFloatingVehiclesVariables>(
    FREE_FLOATING_VEHICLES_QUERY,
    {
      variables: {
        systemId: system.id
      }
    }
  );

  const {
    data: prohibitedZonesQueryData,
    loading: prohibitedZonesQueryLoading,
    error: prohibitedZonesQueryError
  } = useQuery<ProhibitedZones>(PROHIBITED_ZONES_QUERY, {
    variables: {
      systemId: system.id
    }
  });

  const freeFloatingEnabled =
    featureTogglesData?.featureToggles?.freeFloatingEnabled;

  // TODO: This is pretty awful, but just mirroring what has
  // already been done as time is short. Needs refactoring.
  const freeFloatingDots = useMemo(() => {
    if (!freeFloatingVehiclesQueryData?.freeFloatingVehicles) return;

    const { freeFloatingVehicles } = freeFloatingVehiclesQueryData;

    // Show only available vehicles
    const freeFloatingVehiclesFiltered = freeFloatingVehicles.filter(
      vehicle => vehicle.available !== false
    );

    return freeFloatingVehiclesFiltered.reduce<CoordinatesProperties[]>(
      (res, vehicle) => {
        if (!vehicle) return res;

        if (
          !vehicle.location?.location?.lat ||
          !vehicle.location?.location?.lat
        )
          return res;

        const properties: {
          id: string;
          name: string;
          number: string;
          batteryCharge: number | null;
          maxRange: number | null;
          currentRange: number | null;
          category: VehicleCategory2 | null;
          available: boolean | null;
        } = {
          id: vehicle.id,
          name: vehicle.name || '',
          number: vehicle.number || '',
          batteryCharge: vehicle.batteryCharge || null,
          maxRange: vehicle.maxRange || null,
          currentRange: vehicle.currentRange || null,
          category: vehicle.category || null,
          available: vehicle.available || null
        };

        res.push({
          coordinates: [
            vehicle.location.location.lng || 0,
            vehicle.location.location.lat || 0
          ],
          properties
        });

        return res;
      },
      []
    );
  }, [freeFloatingVehiclesQueryData?.freeFloatingVehicles, filters]);

  const prohibitedZones = useMemo(() => {
    if (!prohibitedZonesQueryData?.areas) return;

    const { areas } = prohibitedZonesQueryData;

    const activeAreas = areas.filter(area =>
      area.configurations.some(
        config =>
          config.__typename === 'AreaTripEnd' &&
          config.schedules.some(schedule => isAreaWithinTheSchedule(schedule))
      )
    );

    return activeAreas.reduce<ProhibitedZoneProperties[]>((acc, area) => {
      if (!area || area.polygon.length === 0) return acc;

      const properties: {
        fillColor: string;
        strokeColor: string;
      } = {
        fillColor: area.fillColour || '#808080',
        strokeColor: area.strokeColour || '#000000'
      };

      const coordinates = area.polygon.map(p => [p.lng || 0, p.lat || 0]);

      acc.push({
        coordinates: coordinates,
        properties
      });

      return acc;
    }, []);
  }, [prohibitedZonesQueryData?.areas]);

  const dockGroupDots = useMemo(() => {
    if (!dockGroupsQueryData?.dockGroups) return;

    const { dockGroups } = dockGroupsQueryData;
    return dockGroups.reduce<CoordinatesProperties[]>((res, dockGroup) => {
      if (!dockGroup) return res;

      if (!dockGroup.coord?.lng || !dockGroup.coord.lat) return res;

      const properties: {
        id: string;
        title: string;
        name: string;
        subTitle: string;
        enabled: boolean;
        state: DockGroupState;
        availableVehicles: number | null;
        availableDocks: number | null;
        filterType: (keyof typeof DEFAULT_FILTERS)[];
        availableVehicleCategories:
          | DockGroups_dockGroups_availabilityInfo_availableVehicleCategories[]
          | null;
      } = {
        id: dockGroup.id,
        title: dockGroup.title || '',
        name: dockGroup.name || '',
        subTitle: dockGroup.subTitle || '',
        enabled: dockGroup.enabled,
        state: dockGroup.state,
        filterType: [],
        availableVehicles: null,
        availableDocks: null,
        availableVehicleCategories: null
      };

      if (dockGroup.availabilityInfo) {
        properties.availableVehicles = calculateAvailableVehicles(
          dockGroup.availabilityInfo,
          filters
        );

        properties.availableDocks = calculateAvailableDocks(
          dockGroup.availabilityInfo,
          filters
        );

        properties.availableVehicleCategories =
          dockGroup.availabilityInfo.availableVehicleCategories;
      }

      res.push({
        coordinates: [dockGroup.coord.lng, dockGroup.coord.lat],
        properties
      });
      return res;
    }, []);
  }, [dockGroupsQueryData?.dockGroups, filters]);

  const boundCoordinates = useMemo(() => {
    if (!dockGroupsQueryData) return;

    const { dockGroups } = dockGroupsQueryData;

    if (dockGroups?.length > 2) {
      const firstDockGroup = dockGroups[0];
      if (!firstDockGroup || !firstDockGroup.coord) {
        return;
      }

      return dockGroups.reduce<LngLat[]>((coordinates, dockGroup) => {
        if (
          dockGroup &&
          dockGroup.coord &&
          dockGroup.coord.lng &&
          dockGroup.coord.lat
        ) {
          coordinates.push({
            lng: dockGroup.coord.lng,
            lat: dockGroup.coord.lat
          });
        }
        return coordinates;
      }, []);
    }

    const systemBoundsRaw = stationMapQueryData?.system?.coords || [];
    if (systemBoundsRaw.length <= 2) return;

    const firstSystemBound = systemBoundsRaw[0];
    if (!firstSystemBound?.lat || !firstSystemBound?.lng) {
      return;
    }

    return systemBoundsRaw.reduce<LngLat[]>((coordinates, coord) => {
      if (coord?.lng && coord?.lat) {
        coordinates.push({
          lng: coord.lng,
          lat: coord.lat
        });
      }
      return coordinates;
    }, []);
  }, [stationMapQueryData?.system, dockGroupsQueryData?.dockGroups, filters]);

  if (
    dockGroupsQueryLoading ||
    featureTogglesLoading ||
    freeFloatingVehiclesLoading ||
    prohibitedZonesQueryLoading
  ) {
    return <Loading />;
  }

  if (
    dockGroupsQueryError ||
    featureTogglesError ||
    freeFloatingVehiclesError ||
    prohibitedZonesQueryError
  ) {
    return <>Could not load data</>;
  }

  if (
    !dockGroupsQueryData?.dockGroups ||
    !featureTogglesData?.featureToggles ||
    !freeFloatingVehiclesQueryData?.freeFloatingVehicles
  ) {
    return null;
  }

  if (SHOW_STATIC_MAP || mapType === 'static') {
    return (
      <div>
        <Alert variant="error" mb={4} mx={4}>
          <FormattedMessage
            id="StationMap.mapboxIsNotSupported"
            defaultMessage="Your browser does not support the dynamic map, either update your browser or download one of our apps for your phone."
          />
        </Alert>
        <StaticStationMap
          mapboxApiAccessToken={TOKEN}
          primaryColor={theme.colors.primary}
          dockGroups={dockGroupsQueryData?.dockGroups || []}
        />
      </div>
    );
  }

  const showLiveMap = system.GBFS;
  const inSeason =
    (stationMapQueryData?.system && stationMapQueryData.system.inSeason) ||
    undefined;

  if (!enableAnimatedTrips) {
    return (
      <Map
        disableMapControls={disableMapControls}
        inSeason={inSeason}
        enableAnimatedTrips={enableAnimatedTrips}
        showLiveMap={showLiveMap || undefined}
        boundCoordinates={boundCoordinates}
        dockGroupCoordinates={dockGroupDots}
        freeFloatingCoordinates={freeFloatingDots}
        freeFloatingEnabled={freeFloatingEnabled}
        prohibitedZones={prohibitedZones}
        view={view}
        filters={filters}
        switchFilterValue={switchFilterValue}
        batteryChargeFilter={batteryChargeFilter}
        handleBatteryChargeFilterChange={handleBatteryChargeFilterChange}
      />
    );
  }

  return (
    <Subscription<completedTripSubscription, completedTripSubscriptionVariables>
      subscription={TRIP_SUBSCRIPTION}
      variables={{ systemId: system.id }}
    >
      {({ data }) => {
        return (
          <Map
            disableMapControls={disableMapControls}
            inSeason={inSeason}
            enableAnimatedTrips={enableAnimatedTrips}
            showLiveMap={showLiveMap || undefined}
            boundCoordinates={boundCoordinates}
            dockGroupCoordinates={dockGroupDots}
            view={view}
            filters={filters}
            switchFilterValue={switchFilterValue}
            batteryChargeFilter={batteryChargeFilter}
            handleBatteryChargeFilterChange={handleBatteryChargeFilterChange}
            mapHeight={mapHeight}
            completedTrip={
              data && data.completedTrip ? data.completedTrip : undefined
            }
          />
        );
      }}
    </Subscription>
  );
};

export default StationMap;
